'use client';

import { Palette } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupStoreFormRow } from '@/components/setup/experience/SetupStoreFormRow';
import { setupManualStoreDefaults } from '@/components/setup/setupManualSchemas';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DEFAULT_COLOR = setupManualStoreDefaults.primaryColor;

function isValidHexColor(hex: string): boolean {
  return /^#?[0-9A-Fa-f]{6}$/.test(hex.trim());
}

function getHexDigits(hex: string): string {
  return hex
    .trim()
    .replace(/^#/, '')
    .replace(/[^0-9A-Fa-f]/g, '')
    .slice(0, 6)
    .toUpperCase();
}

export function SetupStoreColorField() {
  const { t } = useTranslation('setup');
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<{ primaryColor: string }>();

  const primaryColor = watch('primaryColor') || DEFAULT_COLOR;
  const previewColor = isValidHexColor(primaryColor) ? primaryColor : DEFAULT_COLOR;
  const errorMessage = errors.primaryColor?.message;

  const handleHexChange = (value: string) => {
    const digits = getHexDigits(value);
    setValue('primaryColor', digits.length > 0 ? `#${digits}` : DEFAULT_COLOR, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleHexBlur = () => {
    setValue(
      'primaryColor',
      isValidHexColor(primaryColor) ? `#${getHexDigits(primaryColor)}` : DEFAULT_COLOR,
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <SetupStoreFormRow
      label={t('experience.connect.storeForm.colorLabel')}
      htmlFor="setup-store-color-hex"
      icon={Palette}
      hint={t('experience.connect.storeForm.colorHint')}
      error={errorMessage}
    >
      <div className="flex items-center justify-end gap-3">
        <input
          type="color"
          value={previewColor}
          onChange={(event) =>
            setValue('primaryColor', event.target.value.toUpperCase(), {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          aria-label={t('experience.connect.storeForm.colorPickerLabel')}
          className="size-11 shrink-0 cursor-pointer appearance-none rounded-lg border-0 bg-transparent p-0 [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-0"
        />

        <div
          className={cn(
            'border-input flex h-11 items-center overflow-hidden rounded-lg border bg-[color-mix(in_srgb,var(--muted)_45%,var(--card))] focus-within:ring-1 focus-within:ring-[var(--ring)]',
            errorMessage && 'border-destructive',
          )}
        >
          <span
            className="text-muted-foreground border-input flex h-full items-center border-r px-2.5 text-sm"
            aria-hidden
          >
            #
          </span>
          <Input
            id="setup-store-color-hex"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            maxLength={6}
            value={getHexDigits(primaryColor)}
            onChange={(event) => handleHexChange(event.target.value)}
            onBlur={handleHexBlur}
            aria-label={t('experience.connect.storeForm.colorHexLabel')}
            aria-invalid={!!errorMessage}
            className="h-full w-24 border-0 bg-transparent font-mono text-sm uppercase tracking-[0.06em] shadow-none focus-visible:ring-0"
          />
        </div>
      </div>
    </SetupStoreFormRow>
  );
}
